import Button from '@components/button/Button';
import Modal from '@components/modal/Modal';
import {
  GetProp,
  Skeleton,
  Tooltip,
  Typography,
  Upload,
  UploadProps,
  message,
} from 'antd';
import ImgCrop from 'antd-img-crop';
import cx from 'classnames';
import { keys } from 'ramda';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdCheck, MdOutlineImage } from 'react-icons/md';
import { connect } from 'react-redux';

import { updateUserConfig } from '../../actions';
import styles from './_BackgroundSettings.module.scss';

const { Text } = Typography;

export interface BackgroundSetting {
  type?: 'color' | 'template' | 'photo';
  value?: string | null;
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

type IBackgroundSettings = {
  open: boolean;
  setOpen: (open: boolean) => void;
  userBackgroundSetting: BackgroundSetting;
  setUserBackgroundSetting: (userBackgroundColor: BackgroundSetting) => void;
  updateUserConfigAction: (value: any) => void;
  removeBackgroundImageFirebase: () => Promise<void>;
  uploadBgImageFirebase: (file: File) => Promise<void>;
  userFirebaseImageUrl: string | null;
  isImageUrlLoading: boolean;
};

export const backgroundTemplateMappings: Record<string, string> = {
  backgroundDefault: '/images/background-default.jpg',
  background12: '/images/background-sample-12.jpg',
  background1: '/images/background-sample-1.jpg',
  background2: '/images/background-sample-2.jpg',
  background3: '/images/background-sample-3.jpg',
  background4: '/images/background-sample-4.jpg',
  background5: '/images/background-sample-5.jpg',
  background6: '/images/background-sample-6.jpg',
  background7: '/images/background-sample-7.jpg',
  background8: '/images/background-sample-8.jpg',
  background9: '/images/background-sample-9.jpg',
  background10: '/images/background-sample-10.jpg',
  background11: '/images/background-sample-11.jpg',
  background13: '/images/background-sample-13.jpg',
  background14: '/images/background-sample-14.jpg',
};

const colorThemes = [
  '#F9FAFB',
  '#E5E7EB',
  '#ABCDFE',
  '#A7F3D0',
  '#C7D2FE',
  '#FEF9C3',
  '#FFE4C7',
  '#FCA5A5',
  '#1E3A8A',
  '#0F172A',
  '#374151',
  '#065F46',
  '#4B164C',
  '#0E7490',
  '#78350F',
];

export const BACKGROUND_TYPES = {
  color: 'color',
  template: 'template',
  photo: 'photo',
} as const;

export const DEFAULT_BACKGROUND = {
  value: 'backgroundDefault',
  type: BACKGROUND_TYPES.template,
};

const BackgroundSettings: React.FC<IBackgroundSettings> = ({
  open,
  setOpen,
  userBackgroundSetting,
  setUserBackgroundSetting,
  updateUserConfigAction,
  uploadBgImageFirebase,
  removeBackgroundImageFirebase,
  userFirebaseImageUrl,
  isImageUrlLoading,
}) => {
  const [activeColorMode, setActiveColorMode] = React.useState(false);

  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const handleImageLoad = useCallback((background: string) => {
    setLoadedImages((prev) => ({
      ...prev,
      [background]: true,
    }));
  }, []);

  const { t } = useTranslation('common', {
    keyPrefix: 'chaosComponents.backgroundSettings',
  });

  const beforeUpload = useCallback(
    (file: FileType) => {
      const isAllowedType =
        file.type === 'image/png' || file.type === 'image/jpeg';
      if (!isAllowedType) {
        message.error(t('imageTypeAllowedMsg')).then();
        return Upload.LIST_IGNORE;
      }

      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error(t('imageSizeAllowedMsg')).then();
        return Upload.LIST_IGNORE;
      }

      return true;
    },
    [t],
  );

  const onSaveBackgroundChange = useCallback(
    (backgroundSetting: BackgroundSetting) => {
      setUserBackgroundSetting(backgroundSetting);
      updateUserConfigAction({ backgroundSetting });
    },
    [setUserBackgroundSetting, updateUserConfigAction],
  );

  const renderColorThemeBackgrounds = useCallback(
    () => (
      <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-bg-white tw-rounded-lg tw-p-3">
        {colorThemes.map((theme) => (
          <div
            key={theme}
            onClick={() => {
              if (userBackgroundSetting?.value !== theme) {
                onSaveBackgroundChange({
                  type: BACKGROUND_TYPES.color,
                  value: theme,
                });
              }
            }}
            className="tw-relative"
          >
            <div
              className={cx('tw-h-14 tw-rounded-xl hover:tw-cursor-pointer', {
                'tw-ring-2 tw-ring-[var(--color-accent)]':
                  userBackgroundSetting?.value === theme,
              })}
              style={{ background: theme }}
            />
            {userBackgroundSetting?.value === theme && (
              <div className="tw-absolute -tw-top-1.5 -tw-right-1.5 tw-bg-[var(--color-accent)] tw-rounded-full tw-w-4 tw-h-4">
                <MdCheck color="white" size={16} />
              </div>
            )}
          </div>
        ))}
      </div>
    ),
    [onSaveBackgroundChange, userBackgroundSetting?.value],
  );

  const renderSampleBackgrounds = useCallback(
    () => (
      <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-bg-white tw-rounded-lg tw-p-3">
        {keys(backgroundTemplateMappings).map((background) => (
          <div
            key={background}
            onClick={() => {
              if (userBackgroundSetting?.value !== background) {
                onSaveBackgroundChange({
                  type: BACKGROUND_TYPES.template,
                  value: background,
                });
              }
            }}
            className="tw-relative"
          >
            {!loadedImages[background] && (
              <Skeleton.Image
                active
                style={{ height: 56, borderRadius: 12, width: 110 }}
                className="tw-absolute tw-inset-0"
              />
            )}
            <img
              src={backgroundTemplateMappings[background]}
              alt={`chaos-${background}`}
              onLoad={() => handleImageLoad(background)}
              className={cx(
                'tw-object-cover tw-h-14 tw-w-full tw-rounded-xl hover:tw-cursor-pointer',
                {
                  'tw-ring-2 tw-ring-[var(--color-accent)]':
                    userBackgroundSetting?.value === background,
                },
              )}
            />
            {userBackgroundSetting?.value === background && (
              <div className="tw-absolute -tw-top-1.5 -tw-right-1.5 tw-bg-[var(--color-accent)] tw-rounded-full tw-w-4 tw-h-4">
                <MdCheck color="white" size={16} />
              </div>
            )}
          </div>
        ))}
      </div>
    ),
    [
      handleImageLoad,
      loadedImages,
      onSaveBackgroundChange,
      userBackgroundSetting?.value,
    ],
  );

  const onBackgroundChangeDone = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onCancelBackgroundChange = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const renderBackgroundTypeSelection = useCallback(
    () => (
      <div className="tw-flex tw-w-full tw-bg-[#cfd1c7] tw-rounded-lg tw-p-2">
        <Button
          className="tw-w-full !tw-rounded"
          type="linkNeutral"
          label={t('photosButton')}
          onClick={() => {
            setActiveColorMode(false);
          }}
          active={!activeColorMode}
        />
        <Button
          className="tw-w-full !tw-rounded"
          type="linkNeutral"
          label={t('colorsButton')}
          onClick={() => setActiveColorMode(true)}
          active={activeColorMode}
        />
      </div>
    ),
    [activeColorMode, t],
  );

  const onCustomRequest = useCallback(
    async ({ file }: any) => {
      try {
        await uploadBgImageFirebase(file as File);
        onSaveBackgroundChange({
          type: BACKGROUND_TYPES.photo,
        });
      } catch (_error) {
        onSaveBackgroundChange(DEFAULT_BACKGROUND);
      }
    },
    [onSaveBackgroundChange, uploadBgImageFirebase],
  );

  const renderUserBackgroundUpload = useCallback(
    () => (
      <>
        <div className="tw-flex tw-justify-between tw-items-center">
          <Text>{t('useYourOwnPhoto')}</Text>
          <div className="tw-flex">
            {userFirebaseImageUrl && (
              <Button
                loading={isImageUrlLoading}
                type="link"
                label={t('removeText')}
                onClick={async () => {
                  await removeBackgroundImageFirebase();
                  if (userBackgroundSetting?.type === BACKGROUND_TYPES.photo) {
                    onSaveBackgroundChange(DEFAULT_BACKGROUND);
                  }
                }}
              />
            )}
            <ImgCrop rotationSlider aspect={16 / 9} modalTitle="Crop Image">
              <Upload
                beforeUpload={beforeUpload}
                showUploadList={false}
                customRequest={onCustomRequest}
              >
                <Button
                  loading={isImageUrlLoading}
                  type="link"
                  label={t('uploadText')}
                />
              </Upload>
            </ImgCrop>
          </div>
        </div>
        <div className="tw-flex tw-w-full tw-relative">
          {userFirebaseImageUrl ? (
            <div
              className="tw-w-full tw-relative"
              onClick={() =>
                onSaveBackgroundChange({ type: BACKGROUND_TYPES.photo })
              }
            >
              <img
                className={cx(
                  'tw-w-full tw-h-[54px] tw-object-cover tw-rounded-lg',
                  {
                    'tw-ring-2 tw-ring-[var(--color-accent)]':
                      userBackgroundSetting?.type === BACKGROUND_TYPES.photo,
                  },
                )}
                src={userFirebaseImageUrl}
                alt="Profile Background"
              />
              {userBackgroundSetting?.type === BACKGROUND_TYPES.photo && (
                <div className="tw-absolute -tw-top-1.5 -tw-right-1.5 tw-bg-[var(--color-accent)] tw-rounded-full tw-w-4 tw-h-4">
                  <MdCheck color="white" size={16} />
                </div>
              )}
            </div>
          ) : (
            <ImgCrop rotationSlider aspect={16 / 9} modalTitle="Crop Image">
              <Upload
                className={styles.uploadFullWidth}
                beforeUpload={beforeUpload}
                showUploadList={false}
                customRequest={onCustomRequest}
              >
                <div className="tw-py-4 tw-px-6 tw-flex tw-gap-2 tw-items-center tw-justify-center tw-bg-white hover:tw-cursor-pointer tw-text-weaker">
                  <MdOutlineImage className="tw-text-inherit" size={16} />
                  <Tooltip title={t('tooltipUploadMsg')} placement="top">
                    <Text className="tw-text-inherit">
                      {t('clickToUpload')}
                    </Text>
                  </Tooltip>
                </div>
              </Upload>
            </ImgCrop>
          )}
        </div>
      </>
    ),
    [
      beforeUpload,
      isImageUrlLoading,
      onCustomRequest,
      removeBackgroundImageFirebase,
      onSaveBackgroundChange,
      t,
      userBackgroundSetting?.type,
      userFirebaseImageUrl,
    ],
  );

  const renderModalContent = useCallback(
    () => (
      <div className="tw-flex tw-flex-col tw-w-[376px] tw-gap-2.5">
        {renderUserBackgroundUpload()}
        {renderBackgroundTypeSelection()}
        {!activeColorMode && renderSampleBackgrounds()}
        {activeColorMode && renderColorThemeBackgrounds()}
        <Button
          type="primary"
          label={t('doneText')}
          onClick={onBackgroundChangeDone}
        />
      </div>
    ),
    [
      activeColorMode,
      onBackgroundChangeDone,
      renderBackgroundTypeSelection,
      renderColorThemeBackgrounds,
      renderSampleBackgrounds,
      renderUserBackgroundUpload,
      t,
    ],
  );

  return (
    <Modal
      isOpen={open}
      title={t('changeBackground')}
      renderFooter={false}
      mask={false}
      onHandleCancel={onCancelBackgroundChange}
      renderModalContent={renderModalContent}
      draggable
    />
  );
};

export default connect(null, {
  updateUserConfigAction: updateUserConfig,
})(BackgroundSettings);
